const _ = require("lodash");

const URLMake = require("../../../shared/URLMake");
const GradeChapter = require("../../../shared/GradeChapter");
const OpsUser = require("../../core/vault/OpsUser");

const {setIn} = require("../../../shared/ImmHelpers");
const {
	atTheaterChapterByPage,
	atTheaterSetByPage,
} = require("../../core/vault/StateCursors");

/* Theater shape {
	videoSets: {
		[videoSetID]: {
			videoSetID,
			model: {
				videoSetID,
				title,
				videoIDs: [],
				videoHashes: [],
				chapterID - inside theater space,
				blockID}
			videoModels: object with videoIDs and {videoID, isWatched, position}
			blockModels: object with blockIDs it's attached to {[blockID]: {displayName, id, chapterID}}
			labBlockModels: object with labBlockIDs it's attached to {[labBlockID]: {displayName, id}}
		}
	}
	chapters: {
		[chapterID]: {
			blocks: [{
				chapterID,
				blockID,
				labBlockID,
				displayName,
				videoSetID,
				model: {percentWatched, lastStartedVideoID},
				firstVideoID,
				firstVideoHash,
			}],
			load,
			loadError,
		}
	}
}}
*/

const setupThrottlerInVault = function(vault) {
	const {api, throttler} = vault;

	throttler.config("videoSet", {
		delay: 10000,
		noLeading: true,
		getID: obj => _.get(obj, "videoSet.videoSetID", 0),
		run: obj => updateVideoSetData(obj.videoSet, obj.userID, api, throttler),
	});
};

const initVideoSet = function(setInfo, demo) {
	const models = {};
	_.each(setInfo.model.videoIDs, vid => {
		const sv = setInfo.videoModels[vid];
		models[vid] = {
			videoID: vid,
			position: (sv && sv.position) || 0,
			isWatched: (sv && sv.isWatched) || false,
		};
	});

	const newSet = _.extend({}, setInfo, {
		videoModels: models,
		demo: !!demo,
	});
	return newSet;
};

const updateVideoSetData = function(videoSet, userID, api, throttler) {
	if (!videoSet.demo) {
		api.updateTheaterData(userID, videoSet.videoSetID, {
			videoModels: videoSet.videoModels,
		});
	}
	throttler.cancel("videoSet", {videoSet, userID});
};

// Currently only updates lastStartedVideoID.
const updateChapterData = function(
	chapter,
	blockID,
	userID,
	api,
	lastStartedVideoID,
	demo
) {
	const blocks = _.get(chapter, "blocks", []);
	const blockIndex = _.findIndex(blocks, b => b.blockID === blockID);
	const videoSetID = _.get(chapter, ["blocks", blockIndex, "videoSetID"]);
	chapter = setIn(
		chapter,
		["blocks", blockIndex, "model", "lastStartedVideoID"],
		lastStartedVideoID
	);

	const data = {lastStartedVideoID};
	if (!demo) {
		api.updateTheaterData(userID, videoSetID, data);
	}
	return chapter;
};

const isValidTheaterGrade = function(page) {
	const gradeNumber = page.gradeNumber;
	return _.includes(["1", "2", "3", "4", "5"], String(gradeNumber));
};

const isValidTheaterChapter = function(page) {
	return GradeChapter.isValidChapterIndex(page.chapterIndex);
};

// Take users to their last visited video in each chapter
// or to the last videos chapter level they last visited.
const fillTheaterPage = function(page, state) {
	// Collapsed Library+Theater remaps /theater landings to library.grade +
	// tab:videos. Reuse the theater.chapter fill, then map coords back so the
	// page stays on the merged Videos tab.
	if (page && page.name === "library.grade" && page.tab === "videos") {
		if (page.demo) return page;
		const filled = fillTheaterPage(
			{
				name: "theater.chapter",
				gradeNumber: page.gradeNumber,
				chapterIndex: page.chapterIndex,
			},
			state
		);
		if (
			filled.gradeNumber === page.gradeNumber &&
			filled.chapterIndex === page.chapterIndex
		) {
			return page;
		}
		return _.extend({}, page, {
			gradeNumber: filled.gradeNumber,
			chapterIndex: filled.chapterIndex,
		});
	}

	return fillTheaterPageHelper(page, state);
};

const fillTheaterPageHelper = function(page, state) {
	if (!page || !_.includes(page.name, "theater")) {
		return page;
	}
	const gradeNumber = page.gradeNumber;
	const chapterIndex = page.chapterIndex;
	const mostRecentPageData = OpsUser.getMostRecentPageData(state.user) || {};
	let defaultPage = URLMake.state.forTheaterFromNumbers(
		mostRecentPageData.gradeNumber || 3,
		mostRecentPageData.chapterIndex || 0
	);
	if (page.demo) defaultPage = URLMake.state.forDemo(defaultPage);
	if (!isValidTheaterGrade(page) || !isValidTheaterChapter(page)) {
		return defaultPage;
	}
	// Check if available chapter in grade
	// Default to last visited theater chapter page.
	if (!GradeChapter.isActiveTheaterIndexPairing(gradeNumber, chapterIndex)) {
		return defaultPage;
	}

	const blockID = page.blockID;
	const videoIndex = page.videoIndex;

	const chapter = atTheaterChapterByPage(state, page).get();
	if (!chapter.blocks) return page;
	const foundBlock = _.find(chapter.blocks, b => b.blockID === blockID);
	if (!foundBlock) {
		let pageData = URLMake.state.forTheaterFromNumbers(
			gradeNumber,
			chapterIndex
		);
		if (page.demo) pageData = URLMake.state.forDemo(pageData);
		return pageData;
	}
	const model = _.get(foundBlock, "model", {});

	const block = atTheaterSetByPage(state, page).get();
	if (!block.videoSet) return page;
	const videoIDs = _.get(block, "videoSet.videoIDs", []);

	if (
		!_.isInteger(videoIndex) ||
		videoIDs.length <= videoIndex ||
		videoIndex < 0
	) {
		const lastVideoIndex = videoIDs.indexOf(model.lastStartedVideoID);
		let pageData = URLMake.state.forTheaterFromNumbers(
			gradeNumber,
			chapterIndex,
			blockID,
			Math.max(0, lastVideoIndex)
		);
		if (page.demo) pageData = URLMake.state.forDemo(pageData);
		return pageData;
	}

	let pageObj = _.extend(
		page,
		URLMake.state.forTheaterFromNumbers(
			gradeNumber,
			chapterIndex,
			blockID,
			videoIndex
		)
	);
	if (page.demo) pageObj = URLMake.state.forDemo(pageObj);

	return _.isEqual(page, pageObj) ? page : pageObj;
};

const getVideoIDFromPage = function(state, page) {
	const theaterSet = atTheaterSetByPage(state, page).get();
	const videoIDs = _.get(theaterSet, "model.videoIDs", []);
	return videoIDs[page.videoIndex || 0];
};

const getLastStartedVideoIDFromSet = function(state, page) {
	const chapter = atTheaterChapterByPage(state, page).get();
	const studentVideoSet = _.get(chapter, ["blocks", page.blockID, "model"], {});
	return studentVideoSet.lastStartedVideoID;
};

module.exports = {
	setupThrottlerInVault,
	initVideoSet,
	updateVideoSetData,
	fillTheaterPage,
	getLastStartedVideoIDFromSet,
	getVideoIDFromPage,
	updateChapterData,
};
